import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, IconButton, Tooltip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { API_CONFIG } from '../../config';
import { useLanguage } from '../../i18n/LanguageContext';

interface UserRow {
  email: string;
  name: string;
  status: string;
  is_admin: boolean;
  subscription_status?: string;
}

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' => {
  if (s === 'active') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'suspended') return 'error';
  return 'default';
};

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(API_CONFIG.endpoints.admin.users);
      setUsers(resp.data.users);
    } catch {
      setError(t.adminActionFailed);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleApprove = async (email: string) => {
    try {
      await api.post(API_CONFIG.endpoints.admin.approve(email));
      setSuccess(t.userApproved);
      fetchUsers();
    } catch { setError(t.adminActionFailed); }
  };

  const handleSuspend = async (email: string) => {
    try {
      await api.post(API_CONFIG.endpoints.admin.suspend(email));
      setSuccess(t.userSuspended);
      fetchUsers();
    } catch { setError(t.adminActionFailed); }
  };

  const displayed = statusFilter
    ? users.filter(u => u.status === statusFilter)
    : users;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">{t.adminUsers}</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t.filterByStatus}</InputLabel>
          <Select
            value={statusFilter}
            label={t.filterByStatus}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="active">{t.statusActive}</MenuItem>
            <MenuItem value="pending">{t.statusPending}</MenuItem>
            <MenuItem value="suspended">{t.statusSuspended}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {displayed.length === 0 ? (
        <Typography color="text.secondary">{t.noUsersFound}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.email}</TableCell>
                <TableCell>{t.status}</TableCell>
                <TableCell>{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.map(user => (
                <TableRow key={user.email} hover>
                  <TableCell>
                    {user.name}
                    {user.is_admin && (
                      <Chip label={t.isAdmin} size="small" color="primary" sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={t[`status${user.status.charAt(0).toUpperCase() + user.status.slice(1)}` as keyof typeof t] as string || user.status}
                      color={statusColor(user.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t.approve}>
                      <span>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleApprove(user.email)}
                          disabled={user.status === 'active'}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={user.status === 'suspended' ? t.reactivate : t.suspend}>
                      <span>
                        <IconButton
                          size="small"
                          color={user.status === 'suspended' ? 'success' : 'warning'}
                          onClick={() => user.status === 'suspended' ? api.post(API_CONFIG.endpoints.admin.reactivate(user.email)).then(() => { setSuccess(t.userReactivated); fetchUsers(); }) : handleSuspend(user.email)}
                          disabled={user.is_admin}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t.edit}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/users/${encodeURIComponent(user.email)}`)}
                      >
                        <PersonIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminUsers;
